import { ECareerLandingPageTabs, thunkActionLoading } from '@configs'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAppDispatch, useAppSelector } from '@redux'
import { message } from 'antd'
import { t } from 'i18next'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { ECareerBannerType } from 'src/interfaces/career-landing-management'
import { IMediaItem } from 'src/interfaces/gallery'
import {
  createCareerAboutLlcAction,
  getCareerAboutLlcAction,
} from 'src/redux/actions/career-landing-management'
import { selectEvolveDownloadCreateLoading } from 'src/redux/Slices/evolveLanding-management'
import { aboutLLCSchema, IAboutLLCSchema } from './aboutLLCSchema'

interface IDownloadProps {
  activeTab?: string
}

export const useAboutLLC = ({ activeTab }: IDownloadProps) => {
  const dispatch = useAppDispatch()
  const [isModalLoading, setIsModalLoading] = useState(false)
  const [isChange, setIsChange] = useState(false)

  const [showModalSelectMedia, setShowModalSelectMedia] = useState(false)
  const [selectedField, setSelectedField] = useState<
    'desktop' | 'mobile' | null
  >(null)
  const onChangeDesktopRef = useRef<any>(null)
  const onChangeMobileRef = useRef<any>(null)

  // Redux selectors
  const aboutLLC = useAppSelector((state) => state.careerLanding.aboutLLC)

  const getAboutLlcLoading = useAppSelector(
    (state) =>
      state.careerLanding.loadings[thunkActionLoading.GET_ABOUT_LLC_LOADING]
  )
  const createAboutLLcLoading = useAppSelector(
    (state) =>
      state.careerLanding.loadings[thunkActionLoading.CREATE_ABOUT_LLC_LOADING]
  )

  const landingUploadImageLoading = useAppSelector(
    (state) => state.aboutUsManagement.loadings?.uploadLandingImageActionLoading
  )

  const [imageSrc, setImageSrc] = useState('')
  const [imageMobileSrc, setImageMobileSrc] = useState('')

  const isDisabledSaveButton = useMemo(() => {
    return createAboutLLcLoading || landingUploadImageLoading || false
  }, [createAboutLLcLoading, landingUploadImageLoading])

  const { handleSubmit, control, setValue, getValues } =
    useForm<IAboutLLCSchema>({
      defaultValues: {
        title: '',
        titleInDutch: '',
        subTitle: '',
        subTitleInDutch: '',
        mediaDescription: '',
        mediaDescriptionInDutch: '',
        mediaId: undefined,
        mediaMobileId: undefined,
        type: ECareerBannerType.about_us,
      },
      resolver: zodResolver(aboutLLCSchema),
      mode: 'onSubmit',
      reValidateMode: 'onChange',
    })

  const onSubmit = async (data: IAboutLLCSchema) => {
    try {
      const response = await dispatch(
        createCareerAboutLlcAction(data as any)
      ).unwrap()

      if (response.success) {
        message.success({
          content: response?.message ?? t('common:save_success'),
        })
      }
    } catch (error: any) {
      message.error({
        content: error?.message ?? t('error:something_went_wrong'),
      })
    }
  }

  const onSave = () => {
    handleSubmit(onSubmit)()
    setIsChange(false)
  }

  const onCloseModalSelectMedia = () => {
    setShowModalSelectMedia(false)
  }

  const onSelectMedia = (data: IMediaItem) => {
    if (selectedField === 'desktop') {
      setImageSrc(data?.baseUrl ?? '')
      setValue('mediaId', data?.id ?? 0)
      onChangeDesktopRef.current(data?.id ?? 0)
    } else if (selectedField === 'mobile') {
      setImageMobileSrc(data?.baseUrl ?? '')
      setValue('mediaMobileId', data?.id ?? 0)
      onChangeMobileRef.current(data?.id ?? 0)
    }

    setShowModalSelectMedia(false)
    setIsChange(true)
  }

  useEffect(() => {
    if (activeTab === ECareerLandingPageTabs.ABOUT_LLC) {
      const getDownload = async () => {
        try {
          const responseGetDownload = await dispatch(
            getCareerAboutLlcAction(ECareerBannerType.about_us)
          ).unwrap()

          if (responseGetDownload.success) {
            setIsChange(false)
          }
        } catch (error: any) {
          message.error({
            content: error.message ?? t('error:something_went_wrong'),
          })
        }
      }
      getDownload()
    }
  }, [activeTab, dispatch, setValue])

  useEffect(() => {
    if (aboutLLC) {
      if (Object.keys(aboutLLC).length) {
        setValue('title', aboutLLC?.title || '')
        setValue('titleInDutch', aboutLLC?.titleInDutch || '')
        setValue('subTitle', aboutLLC?.subTitle || '')
        setValue('subTitleInDutch', aboutLLC?.subTitleInDutch || '')
        setValue('mediaDescription', aboutLLC?.mediaDescription || '')
        setValue(
          'mediaDescriptionInDutch',
          aboutLLC?.mediaDescriptionInDutch || ''
        )
        setValue('mediaId', aboutLLC?.mediaId || 0)
        setValue('mediaMobileId', aboutLLC?.mediaMobileId || 0)
        setImageSrc(aboutLLC?.media?.url ?? '')
        setImageMobileSrc(aboutLLC?.mediaMobile?.url ?? '')
      }
    }
  }, [aboutLLC])

  return {
    getAboutLlcLoading,
    control,
    setIsChange,
    onChangeDesktopRef,
    setValue,
    setImageSrc,
    setSelectedField,
    setShowModalSelectMedia,
    imageSrc,
    getValues,
    isModalLoading,
    setIsModalLoading,
    onChangeMobileRef,
    isDisabledSaveButton,
    imageMobileSrc,
    onSave,
    isChange,
    showModalSelectMedia,
    onCloseModalSelectMedia,
    onSelectMedia,
  }
}
